import type { EntityPreview } from '@/lib/visibility/preview';

interface Props {
  preview: EntityPreview;
}

/**
 * Before / after: what a search engine can actually read about this business.
 *
 * This is the part that makes the report worth running. A list of failed checks tells
 * someone they are wrong; this shows them what right looks like, on their own business, in
 * the two places it matters — a search result and an AI answer.
 *
 * HONESTY: every surface here is labelled "what a search engine can read", never "where
 * you will rank". We do not control Google, cannot promise a map-pack slot, and the map
 * pack has three positions that someone else may already hold. The claim is only ever
 * about machine-readability, which is the one thing we can demonstrate.
 *
 * The "after" column never invents a phone number or an address. Those show as explicit
 * "you supply this" placeholders, because a fabricated contact detail in a preview would be
 * worse than an honest blank.
 */
export default function VisibilityPreview({ preview }: Props) {
  return (
    <section aria-labelledby="preview-heading">
      <h2 id="preview-heading" className="text-xl font-bold tracking-tight text-slate-900">
        What a search engine can read about you
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Same business, before and after the fixes above. This is what a machine can extract
        from your website &mdash; not a prediction of where you will rank.
      </p>

      {/* AI answer */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          What an AI assistant would say
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-500">Today</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              &ldquo;{preview.beforeSentence}&rdquo;
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Almost nothing. An assistant cannot tell what you do, where you are, or how to
              reach you &mdash; so it will recommend someone else.
            </p>
          </div>
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-semibold text-emerald-700">After the fixes</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-800">
              &ldquo;{preview.afterSentence}&rdquo;
            </p>
            <p className="mt-2 text-xs text-emerald-700">
              Accurate, specific and quotable &mdash; which is what gets a business named in an
              answer.
            </p>
          </div>
        </div>
      </div>

      {/* Fact grid */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Facts a search engine can read from your website, before and after
          </caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              <th scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                What a machine reads
              </th>
              <th scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Today
              </th>
              <th scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                After the fixes
              </th>
            </tr>
          </thead>
          <tbody>
            {preview.after.map((fact, index) => {
              const before = preview.before[index];
              return (
                <tr key={fact.label} className="border-b border-slate-100 align-top last:border-0">
                  <th scope="row" className="px-4 py-2.5 text-left font-medium text-slate-800">
                    {fact.label}
                  </th>
                  <td className="px-4 py-2.5 text-slate-500">
                    {before?.value ? (
                      <span className="text-slate-700">{truncate(before.value)}</span>
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-wide text-red-500">
                        Nothing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {fact.value ? (
                      <span className={fact.filled ? 'text-emerald-800' : 'text-slate-800'}>
                        {truncate(fact.value, 70)}
                        {fact.filled ? (
                          <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
                            added
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">&mdash;</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-sm font-semibold text-slate-800">
        {preview.filledCount} of {preview.totalCount} facts go from empty to complete.
      </p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Marked <span className="font-semibold">added</span> means the fixes supply it, or that
        you supply one detail and the rest is generated. Nothing here invents a phone number or
        an address you have not given us.
      </p>
    </section>
  );
}

function truncate(value: string, max = 46): string {
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}\u2026` : value;
}